import { Blur, CbhIcon, LoadingSpinner, Pill, Text } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { formatCentsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useMemo } from "react";
import { useInView } from "react-intersection-observer";

import { ShiftNameType } from "../../PlacementCandidate/types";
import { type PayRateMeta, type PlacementForComputedPay } from "../lib/useComputedPay";
import { type PayTransparencyFlag } from "../lib/usePayTransparencyFlag";
import { type PayRate } from "../types/payRate";
import { formatPayRange } from "../utils";
import { useSavedPayAndScheduleFormData } from "./PayAndScheduleForm/usePayAndScheduleForm";

interface PlacementCardPayProps {
  placement: PlacementForComputedPay;
  placementCandidateId: string;
  payRate: PayRate | undefined;
  payRateMeta: PayRateMeta | undefined;
  label: string | undefined;
  loadingPayData: boolean;
  payRateExperiment: PayTransparencyFlag;
}

export function PlacementCardPay(props: PlacementCardPayProps) {
  const {
    payRate,
    payRateMeta,
    label,
    loadingPayData,
    placement,
    placementCandidateId,
    payRateExperiment,
  } = props;

  const { userId: workerId } = useDefinedWorker();
  const isBlurred = payRate?.externallyDerived && payRate?.obscured;
  const rateIsVisible = (payRate?.exact ?? payRate?.max ?? payRate?.min ?? 0) > 0;

  const showExternalPayRate = payRateExperiment.enabled && payRate?.externallyDerived && !isBlurred;

  const [payAndScheduleFormData] = useSavedPayAndScheduleFormData();

  const minMaxPay = useMemo(
    () =>
      showExternalPayRate
        ? payRateMeta?.computeMinMaxPay(
            payAndScheduleFormData.yearsOfExperience,
            payAndScheduleFormData.useCombinedBenefit
          )
        : undefined,
    [payRateMeta, showExternalPayRate, payAndScheduleFormData]
  );

  const payRateForAnalytics = minMaxPay
    ? {
        min: minMaxPay.min,
        max: minMaxPay.max,
      }
    : {
        min: payRate?.min,
        max: payRate?.max,
        exact: payRate?.exact,
      };

  const { ref } = useInView({
    triggerOnce: true,
    threshold: 0,
    onChange: (inView) => {
      if (!inView) {
        return;
      }

      logEvent(APP_V2_APP_EVENTS.PLACEMENT_PAY_VIEWED, {
        workerId,
        placementCandidateId,
        workplaceId: placement.workplaceId,
        placementId: placement.id,
        isBlurred,
        payRateExperiment: payRateExperiment.enabled,
        payRate: payRateForAnalytics,
      });
    },
  });

  const payRateText = (() => {
    if (!payRate) {
      return undefined;
    }

    if (showExternalPayRate && minMaxPay) {
      return minMaxPay.min === minMaxPay.max
        ? formatCentsAsUsd(minMaxPay.min)
        : `${formatCentsAsUsd(minMaxPay.min)} - ${formatCentsAsUsd(minMaxPay.max)}`;
    }

    return formatPayRange({
      payRate,
      showPerHour: false,
      // these differential traits are not actually used here. this code should be cleaned up.
      differentialTraits: {
        shiftTypes: [ShiftNameType.AM],
        benefits: {
          combined: true,
          health: true,
          pto: true,
        },
        isWeekendShift: false,
      },
    });
  })();

  return (
    <Stack direction="column" spacing={1}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        flexWrap="nowrap"
        flexShrink={0}
        spacing={2}
      >
        {loadingPayData ? (
          <LoadingSpinner size="small" />
        ) : (
          <>
            {label && !showExternalPayRate && (
              <Stack ref={ref}>
                <Text
                  variant="body2"
                  color={(theme) =>
                    showExternalPayRate
                      ? theme.palette.intent?.success.text
                      : theme.palette.intent?.disabled.text
                  }
                >
                  {label}
                </Text>
              </Stack>
            )}
            {payRate && rateIsVisible && payRateText && (
              <Stack direction="row" alignItems="baseline">
                <Text
                  semibold
                  variant="h5"
                  color={(theme) =>
                    showExternalPayRate ? theme.palette.intent?.success.text : undefined
                  }
                >
                  {payRateText}
                </Text>

                <Text
                  variant="body2"
                  color={(theme) =>
                    showExternalPayRate ? theme.palette.intent?.success.text : undefined
                  }
                >
                  &nbsp;/hr
                </Text>
              </Stack>
            )}

            {isBlurred && (
              <Stack direction="row" alignItems="center">
                <Text semibold variant="h5">
                  $
                </Text>
                <Blur sx={{ width: "80px", height: "1.4rem" }} />
              </Stack>
            )}
          </>
        )}
      </Stack>
      {showExternalPayRate ? (
        <Pill
          label={
            <Stack direction="row" spacing={1} alignItems="center">
              <Text variant="body2">View pay and availability</Text>
              <CbhIcon size="small" type="chevron-right" />
            </Stack>
          }
          color={(theme) => theme.palette.intent?.success.text}
          backgroundColor={(theme) => theme.palette.intent?.success.background}
        />
      ) : undefined}
    </Stack>
  );
}
